import { ResponsiveEyeSelection, NavButtons, Flex, Text, theme, RemoteVideo } from '../../../2022-component-library';
import { useState, useEffect, useCallback } from 'react';
import { useTestContext } from '../../../context/testContext';
import { ResponsiveRefractionTestOptician, ResponsiveRefractionResults } from '.';
import { ibisVisionToolOptions } from '../../../helpers/refraction';
import { useMediaQuery } from '../../../helpers/useMediaQuery';
import { useReceiveMessage, usePostMessage } from '../../../context/messageContext';
import { useResultData } from '../../../helpers/useResultData';

const ChangeEyePrompt = () => {
  const videoSidebarShowing = useMediaQuery('(orientation: landscape) and (min-width: 1000px)');

  return (
    <Flex column centered width='70vw' maxWidth='500px' marginBottom='1rem'>
      {!videoSidebarShowing && (
        <Flex
          width='20vw'
          height='20vw'
          minWidth='133px'
          minHeight='133px'
          backgroundColor='lightblue'
          borderRadius='4px'
          boxShadow={theme.elevations.elevation4}
          marginBottom='2rem'
          centered
        >
          <RemoteVideo />
        </Flex>
      )}
      <Text textAlign='center' marginBottom='1rem'>
        {'Now prompt the patient to move to the next eye.'}
      </Text>
      <Text textAlign='center'>{'Click "Next" to proceed to the next eye test when you are ready.'}</Text>
    </Flex>
  );
};

const ResponsiveRefractionContainer = ({ setActiveTab, setTestStep, patientResponse }) => {
  const { testData, updateTestData } = useTestContext();
  const [step, setStep] = useState('eyeSetup');
  const [nextStep, setNextStep] = useState('');
  const [backStep, setBackStep] = useState('');
  const [canProgress, setCanProgress] = useState(false);
  const { clearCurrentEyeResults } = useResultData();
  const receivedMessage = useReceiveMessage();
  const postMessage = usePostMessage();

  const updateStep = useCallback((selectedStep) => {
    setCanProgress(false);
    if (selectedStep === 'selectTest') {
      setTestStep('selectTest');
      postMessage('holding');
    } else {
      setBackStep(step);
      if (selectedStep.endsWith('Setup') || selectedStep === 'changeEyePrompt') postMessage({ type: 'holding' });
      if (selectedStep === 'results') postMessage({ type: 'endOfTest' });
      if (selectedStep.endsWith('Test')) {
        postMessage({
          type: 'startRefraction',
          content: {},
        });
      }
      setStep(selectedStep);
    }
  }, []);

  useEffect(() => {
    if (!step.endsWith('Test') || step.startsWith('eye')) return;
    const eyeTested = step.split('Test')[0];
    updateTestData({ [eyeTested]: { selectedTool: ibisVisionToolOptions[0] } });
  }, [updateTestData, step]);

  const renderSwitch = () => {
    switch (step) {
      case 'eyeSetup':
        return <ResponsiveEyeSelection setCanProgress={setCanProgress} />;
      case 'changeEyePrompt':
        return <ChangeEyePrompt />;
      case 'rightEyeTest':
      case 'leftEyeTest':
      case 'bothEyesTest':
        return (
          <ResponsiveRefractionTestOptician
            patientResponse={patientResponse}
            eyeTested={step.split('Test')[0]}
            updateStep={(step) => updateStep(step)}
            setCanProgress={setCanProgress}
          />
        );
      case 'results':
        return <ResponsiveRefractionResults updateStep={(step) => updateStep(step)} setActiveTab={setActiveTab} />;
      default:
        return;
    }
  };

  useEffect(() => {
    let selectedEye;
    switch (true) {
      case step === 'eyeSetup':
        updateTestData({ testInProgress: false });
        setBackStep('selectTest');
        setNextStep(testData.testBothEyes ? 'bothEyesTest' : testData.testRightEye ? 'rightEyeTest' : 'leftEyeTest');
        break;
      case step === 'changeEyePrompt':
        setCanProgress(true);
        setBackStep('rightEyeTest');
        setNextStep('leftEyeTest');
        break;
      case step.endsWith('Test'):
        selectedEye = step.split('Test')[0];
        setNextStep(selectedEye === 'rightEye' && testData.testLeftEye ? 'changeEyePrompt' : 'results');
        setBackStep(selectedEye === 'leftEye' && testData.testRightEye ? 'changeEyePrompt' : 'eyeSetup');
        break;
      default:
        break;
    }
  }, [step, testData.testRightEye, testData.testLeftEye, testData.testBothEyes, updateTestData, clearCurrentEyeResults]);

  useEffect(() => {
    if (receivedMessage?.type === 'beginTestAgain') updateStep('eyeSetup');
  }, [receivedMessage, updateStep]);

  return (
    <Flex column centered>
      {renderSwitch()}
      {step !== 'results' && (
        <NavButtons marginTop='0.5rem' updateStep={(direction) => updateStep(direction === 'back' ? backStep : nextStep)} canProgress={canProgress} />
      )}
    </Flex>
  );
};

export default ResponsiveRefractionContainer;
